import { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Payment as PaymentIcon,
  LocalShipping as ShippingIcon,
  MonetizationOn as CashIcon,
  CreditCard as CardIcon,
  AccountBalance as BankIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import CartService from '../services/CartService';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import OrderService from '../services/OrderService';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import NotificationService from '../services/NotificationService';

const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDMTA0LjQxOCA3MCAxMDggNjYuNDE4MyAxMDggNjJDMTA4IDU3LjU4MTcgMTA0LjQxOCA1NCAxMDAgNTRDOTUuNTgxNyA1NCA5MiA1Ny41ODE3IDkyIDYyQzkyIDY2LjQxODMgOTUuNTgxNyA3MCAxMDAgNzBaIiBmaWxsPSIjQTBBMEEwIi8+CjxwYXRoIGQ9Ik0xNDAgMTQwVjEzNkMxNDAgMTI0Ljk1NCAxMzEuMDQ2IDExNiAxMjAgMTE2SDgwQzY4Ljk1NDMgMTE2IDYwIDEyNC45NTQgNjAgMTM2VjE0MEgxNDBaIiBmaWxsPSIjQTBBMEEwIi8+Cjwvc3ZnPgo=';

export default function Checkout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  // Define steps and paymentMethods inside the component
  const steps = [
    t('checkout.shippingAddress'),
    t('checkout.paymentMethod'),
    t('checkout.orderSummary')
  ];

  const paymentMethods = [
    { 
      id: 'cash', 
      label: t('checkout.form.cashOnDelivery'), 
      icon: <CashIcon />, 
      description: t('checkout.form.cashDescription') 
    },
    { 
      id: 'card', 
      label: t('checkout.form.creditCard'), 
      icon: <CardIcon />, 
      description: t('checkout.form.cardDescription') 
    },
    { 
      id: 'bank', 
      label: t('checkout.form.bankTransfer'), 
      icon: <BankIcon />, 
      description: t('checkout.form.bankDescription') 
    }
  ];

  const [shippingData, setShippingData] = useState({
    fullName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const queryClient = useQueryClient();

  // Fetch cart data
  const { data: cart, isLoading } = useQuery('cart', CartService.getCart);

  // Update validation schema to use translations
  const validationSchema = Yup.object({
    fullName: Yup.string()
      .required(t('checkout.validation.required')),
    email: Yup.string()
      .email(t('checkout.validation.invalidEmail'))
      .required(t('checkout.validation.required')),
    phone: Yup.string()
      .required(t('checkout.validation.required'))
      .matches(/^\+?[\d\s-]+$/, t('checkout.validation.invalidPhone')),
    address: Yup.string()
      .required(t('checkout.validation.required')),
    city: Yup.string()
      .required(t('checkout.validation.required')),
    state: Yup.string()
      .required(t('checkout.validation.required')),
    zipCode: Yup.string()
      .required(t('checkout.validation.required')),
    country: Yup.string()
      .required(t('checkout.validation.required')),
    cardNumber: Yup.string()
      .required(t('checkout.validation.required'))
      .matches(/^\d{16}$/, t('checkout.validation.invalidCard')),
    expiryDate: Yup.string()
      .required(t('checkout.validation.required'))
      .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, t('checkout.validation.invalidExpiry')),
    cvv: Yup.string()
      .required(t('checkout.validation.required'))
      .matches(/^\d{3,4}$/, t('checkout.validation.invalidCVV'))
  });

  const [shippingErrors, setShippingErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const validateField = (name, value) => {
    if (!value.trim()) {
      setShippingErrors(prev => ({
        ...prev,
        [name]: t('checkout.validation.required')
      }));
      return false;
    }

    // Additional validation based on field type
    switch (name) {
      case 'phone':
        if (!/^\+?[\d\s-]+$/.test(value)) {
          setShippingErrors(prev => ({
            ...prev,
            [name]: t('checkout.validation.invalidPhone')
          }));
          return false;
        }
        break;
      case 'zipCode':
        if (!/^\d{5}(-\d{4})?$/.test(value)) {
          setShippingErrors(prev => ({
            ...prev,
            [name]: t('checkout.validation.invalidZipCode')
          }));
          return false;
        }
        break;
      // Add other specific validations as needed
    }

    setShippingErrors(prev => ({
      ...prev,
      [name]: null
    }));
    return true;
  };

  const handleFieldBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value);
  };

  const handleShippingChange = (event) => {
    const { name, value } = event.target;
    setShippingData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    setShippingErrors(prev => ({
      ...prev,
      [name]: null
    }));
  };

  const validateShippingForm = () => {
    let isValid = true;
    const fields = ['fullName', 'address', 'city', 'state', 'zipCode', 'phone'];
    
    fields.forEach(field => {
      if (!validateField(field, shippingData[field])) {
        isValid = false;
      }
    });

    return isValid;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateShippingForm()) {
      enqueueSnackbar(t('checkout.validation.fillRequired'), { variant: 'warning' });
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const calculateTotal = () => {
    if (!cart?.items?.length) return 0;
    
    return cart.items.reduce((total, item) => {
      const price = item.product?.price || 0;
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  const handlePlaceOrder = async () => {
    if (!isStepValid()) return;

    try {
      setIsProcessing(true);
      const orderData = await processOrder();
      
      // Navigate to success page with order details
      navigate('/order-success', { 
        state: { 
          order: {
            id: orderData,
            paymentMethod,
            shipping: shippingData
          }
        },
        replace: true // Replace current route to prevent going back to checkout
      });

    } catch (error) {
      console.error('Order error:', error);
      enqueueSnackbar(t('checkout.messages.orderError'), { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const processOrder = async () => {
    try {
      // Get current cart items
      const cartData = await CartService.getCart();
      
      if (!cartData.items || cartData.items.length === 0) {
        throw new Error(t('checkout.errors.emptyCart'));
      }

      // Prepare order data
      const orderData = {
        items: cartData.items,
        paymentMethod,
        shipping: shippingData,
        total: calculateTotal()
      };

      // Place order
      const orderId = await OrderService.placeOrder(orderData);
      
      // Clear cart after successful order
      await CartService.clearCart();
      
      return orderId;
    } catch (error) {
      console.error('Order error:', error);
      throw error;
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('checkout.fields.fullName')}
                name="fullName"
                value={shippingData.fullName}
                onChange={handleShippingChange}
                onBlur={handleFieldBlur}
                error={!!shippingErrors.fullName}
                helperText={shippingErrors.fullName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('checkout.fields.address')}
                name="address"
                value={shippingData.address}
                onChange={handleShippingChange}
                onBlur={handleFieldBlur}
                error={!!shippingErrors.address}
                helperText={shippingErrors.address}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.city')}
                name="city"
                value={shippingData.city}
                onChange={handleShippingChange}
                onBlur={handleFieldBlur}
                error={!!shippingErrors.city}
                helperText={shippingErrors.city}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.state')}
                name="state"
                value={shippingData.state}
                onChange={handleShippingChange}
                onBlur={handleFieldBlur}
                error={!!shippingErrors.state}
                helperText={shippingErrors.state}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.zipCode')}
                name="zipCode"
                value={shippingData.zipCode}
                onChange={handleShippingChange}
                onBlur={handleFieldBlur}
                error={!!shippingErrors.zipCode}
                helperText={shippingErrors.zipCode}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('checkout.fields.phone')}
                name="phone"
                value={shippingData.phone}
                onChange={handleShippingChange}
                onBlur={handleFieldBlur}
                error={!!shippingErrors.phone}
                helperText={shippingErrors.phone}
                required
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend">{t('checkout.form.selectPayment')}</FormLabel>
            <RadioGroup
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {paymentMethods.map((method) => (
                <Card
                  key={method.id}
                  sx={{
                    mb: 2,
                    border: paymentMethod === method.id ? `2px solid ${theme.palette.primary.main}` : '1px solid #ddd',
                    cursor: 'pointer'
                  }}
                  onClick={() => setPaymentMethod(method.id)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      value={method.id}
                      control={<Radio />}
                      label=""
                      sx={{ m: 0 }}
                    />
                    {method.icon}
                    <Box>
                      <Typography variant="subtitle1">{method.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {method.description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('checkout.orderSummary')}
            </Typography>
            {cart?.items?.map((item) => (
              <Box key={item.productId} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                  <Box
                    component="img"
                    src={item.product.image || '/placeholder.png'}
                    alt={item.product.title}
                    sx={{
                      width: 50,
                      height: 50,
                      objectFit: 'cover',
                      borderRadius: 1,
                      bgcolor: 'grey.100'
                    }}
                  />
                  
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">
                      {item.product.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('checkout.quantity')}: {item.quantity}
                    </Typography>
                  </Box>
                  
                  <Typography variant="subtitle2">
                    ${(item.product.price * item.quantity).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            ))}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1">{t('checkout.summary.subtotal')}</Typography>
              <Typography variant="subtitle1">${calculateTotal().toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1">{t('checkout.summary.shipping')}</Typography>
              <Typography variant="subtitle1">{t('checkout.free')}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">{t('checkout.summary.total')}</Typography>
              <Typography variant="h6" color="primary">
                ${calculateTotal().toFixed(2)}
              </Typography>
            </Box>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return validateShippingForm();
      case 1:
        return !!paymentMethod;
      case 2:
        return true;
      default:
        return false;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!cart?.items?.length) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          {t('checkout.emptyCart')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom align="center">
          {t('checkout.title')}
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            {t('checkout.buttons.back')}
          </Button>
          <Button
            variant="contained"
            onClick={activeStep === steps.length - 1 ? handlePlaceOrder : handleNext}
          >
            {activeStep === steps.length - 1 
              ? t('checkout.buttons.placeOrder') 
              : t('checkout.buttons.next')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
} 